/**
 * Login Page
 *
 * Sign in with email magic-link
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginLinkRequestSchema } from "@/lib/api";
import { useLoginLink } from "@/lib/api/hooks";
import { useUiStore } from "@/lib/store";

export default function LoginPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const { mutate: login, isPending } = useLoginLink();
  const addNotification = useUiStore((s) => s.addNotification);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: zodResolver(LoginLinkRequestSchema),
    defaultValues: {
      email: "",
      redirectUrl: `${window.location.origin}/dashboard`,
    },
  });

  const onSubmit = (data: any) => {
    login(data, {
      onSuccess: () => {
        setSubmittedEmail(data.email);
        setIsSubmitted(true);
      },
      onError: (error) => {
        if (error.message.includes("not found")) {
          setError("email", {
            type: "server",
            message: "No account found with this email. Try signing up instead.",
          });
        } else {
          addNotification(error.message, "error");
        }
      },
    });
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 text-center max-w-md w-full">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <span className="text-3xl">✓</span>
          </div>
          <h1 className="text-3xl font-bold text-ink mb-2">Check Your Email</h1>
          <p className="text-slate-600">
            We&apos;ve sent a login link to <strong>{submittedEmail}</strong>
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-blue-700 mb-3">
            <strong>What to do next:</strong>
          </p>
          <ol className="text-sm text-blue-700 list-decimal list-inside space-y-2">
            <li>Check your email inbox</li>
            <li>Click the link to sign in</li>
            <li>You&apos;ll be logged in automatically</li>
          </ol>
        </div>

        <p className="text-slate-600 text-sm mb-6">
          The link expires in 15 minutes for your security.
        </p>

        <div className="space-y-3">
          <p className="text-slate-600 text-sm">Didn&apos;t receive an email?</p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="w-full px-6 py-3 border-2 border-accent text-accent rounded-lg hover:bg-blue-50 transition font-semibold"
          >
            Try Again
          </button>
        </div>

        <hr className="my-6" />

        <p className="text-slate-600 text-sm">
          Don&apos;t have an account?{" "}
          <Link to="/auth/signup" className="text-accent hover:underline font-semibold">
            Sign Up
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
      <h1 className="text-3xl font-bold text-ink mb-2">Sign In</h1>
      <p className="text-slate-600 mb-8">Access your boat listings and messages</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
            Email *
          </label>
          <input
            {...register("email")}
            type="email"
            id="email"
            placeholder="you@example.com"
            disabled={isPending}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-slate-900 disabled:bg-slate-100 ${
              errors.email ? "border-red-500" : "border-ash"
            }`}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50 transition font-semibold mt-6"
        >
          {isPending ? "Sending..." : "Send Login Link"}
        </button>
      </form>

      <hr className="my-6" />

      <p className="text-slate-600 text-sm text-center">
        Don&apos;t have an account?{" "}
        <Link to="/auth/signup" className="text-accent hover:text-accent-dark font-semibold">
          Sign Up
        </Link>
      </p>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          <strong>🔒 No password required:</strong> We&apos;ll send you a secure link via email to
          sign in.
        </p>
      </div>
    </div>
  );
}
